// /////////////////////////////////////////////////////////////
// [ファイル] ConfirmDialog.tsx
// [概要] 「確認 → 実行」の汎用モーダルダイアログ。
//        タイトル・本文・キャンセル/実行ボタンを持つ共通の確認 UI。
//        単体でも利用できるが、通常は ConfirmProvider 経由の
//        useConfirm() フックから呼び出す（命令的に await で結果を取得）。
// /////////////////////////////////////////////////////////////
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import * as Icons from '../constants/icons';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
  STANDARD_DANGER_BUTTON_SX,
} from '../constants/buttonStyles';

// 確認ダイアログの表示内容。useConfirm() の引数としても利用する。
export interface ConfirmOptions {
  // ダイアログ見出し（例: 「保存の確認」「削除の確認」）
  title?: string;
  // 本文。文字列のほか任意のノードを渡せる。
  message: React.ReactNode;
  // 実行ボタンのラベル（既定: 「OK」）
  confirmText?: string;
  // キャンセルボタンのラベル（既定: 「キャンセル」）
  cancelText?: string;
  // 実行ボタン左のアイコン（例: <Icons.Save />）
  confirmIcon?: React.ReactNode;
  // 破壊的操作（削除など）。実行ボタンを赤系スタイルにし、見出しアイコンを警告色にする。
  destructive?: boolean;
}

interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// 単体利用も可能な、状態を持たない純粋な表示コンポーネント。
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title = '確認',
  message,
  confirmText = 'OK',
  cancelText = 'キャンセル',
  confirmIcon,
  destructive = false,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: 1,
          minWidth: '340px',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        {destructive && <Icons.WarningAmber sx={{ color: '#c62828' }} />}
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'text.primary' }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} sx={STANDARD_TEXT_BUTTON_SX}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          startIcon={confirmIcon}
          sx={destructive ? STANDARD_DANGER_BUTTON_SX : STANDARD_CTA_BUTTON_SX}
          autoFocus
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
